import type { CatalogMood, Mood } from '@/api/types';
import { cn } from '@/lib/cn';
import type { Accent } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';

export const FALLBACK_MOODS: CatalogMood[] = [
  { id: 'chill', label: 'Chill', description: 'Easy conversation, no pressure.', emoji: '🌙' },
  { id: 'playful', label: 'Playful', description: 'Up for silly questions and games.', emoji: '🎈' },
  { id: 'curious', label: 'Curious', description: 'Big questions, good stories.', emoji: '🔭' },
  { id: 'competitive', label: 'Competitive', description: 'Bring on the games. Keep score.', emoji: '🏆' },
];

/** Picked = a solid fill (docs/DESIGN.md): near-black by default, or a poster field. */
const SEL: Record<Accent, string> = {
  pop: 'bg-primary text-ground',
  zest: 'bg-green text-night',
  volt: 'bg-red text-night',
  sky: 'bg-yellow text-night',
  sun: 'bg-yellow text-night',
  danger: 'bg-danger text-ground',
  paper: 'bg-primary text-ground',
};

export function MoodPicker({
  moods,
  value,
  onChange,
  accent = 'pop',
  compact,
  testIdPrefix = 'mood',
}: {
  moods: CatalogMood[] | undefined;
  value: Mood | null;
  onChange: (m: Mood) => void;
  accent?: Accent;
  compact?: boolean;
  testIdPrefix?: string;
}) {
  const list = moods?.length ? moods : FALLBACK_MOODS;
  return (
    <div role="radiogroup" aria-label="Mood" className={cn('grid gap-2', compact ? 'grid-cols-2 sm:grid-cols-4' : 'sm:grid-cols-2')}>
      {list.map((m) => {
        const on = value === m.id;
        return (
          <button
            key={m.id}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(m.id)}
            data-testid={`${testIdPrefix}-${m.id}`}
            className={cn(
              'relative flex items-center gap-3 text-left transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fg active:scale-[0.96]',
              compact ? 'h-12 justify-center rounded-full px-4' : 'rounded-card p-4 pr-12',
              on ? SEL[accent] : 'bg-surface hover:bg-raised',
            )}
          >
            <span className="min-w-0">
              <span className={cn('block font-semibold', compact ? 'text-[15px]' : 'text-[17px]')}>{m.label}</span>
              {!compact && <span className={cn('mt-0.5 block text-[15px]', on ? 'opacity-75' : 'text-muted')}>{m.description}</span>}
            </span>
            {on && !compact && <Icon name="check" size={20} strokeWidth={2.4} className="absolute right-4 top-1/2 -translate-y-1/2" />}
          </button>
        );
      })}
    </div>
  );
}
